import type {
  DealFixedRateLoanData,
  DealFloatingRateLoanData,
  DealForwardData,
  DealOptionData,
  OTCDealData,
} from '@src/types/OTC';
import {
  isFixedRateLoanDeal,
  isFixedRateLoanDealFormDraft,
  isFloatingRateLoanDeal,
  isFloatingRateLoanDealFormDraft,
  isForwardDeal,
  isForwardDealFormDraft,
  isOptionDeal,
  isOptionDealFormDraft,
} from '@src/types/OTC/otcGuards';
import type { OTCOptionProductTemplate, OTCProductTemplateSecurity } from '@talos/kyoko';
import {
  AllocationValueTypeEnum,
  createDateOnly,
  createDateOnlyToday,
  format,
  isOTCForwardSecurity,
  logger,
  OptionTypeEnum,
  PricingModeEnum,
  StrikeTypeEnum,
} from '@talos/kyoko';
import type { WritableDraft } from 'immer';
import {
  getInitialFormState,
  handleSymbolChange,
  handleTemplateSymbolChange,
  mapAllocationsToField,
  setTouchedValueForAllDealFields,
  validateAllocations,
} from './ManualDealSliceUtils';
import {
  isManualDealFixedRateLoanSymbol,
  isManualDealFloatingRateLoanSymbol,
  isManualDealOptionSymbol,
} from './manualDealSymbolSource';
import type { ManualDealTradeState } from './types';

// Type definitions
type Allocation = {
  subAccount: string;
  value: string;
};

/**
 * Initialize fields for modifying a deal
 */
export function initializeFieldsForModifying(
  state: WritableDraft<ManualDealTradeState>,
  deal: OTCDealData,
  productTemplate: OTCProductTemplateSecurity
): void {
  // Set booking type based on SubProductType
  let bookingType: 'forward' | 'fixedRateLoan' | 'option' | 'floatingRateLoan' = 'forward';
  const subProductType = deal.SubProductType;
  if (subProductType === 'OTCFixedRateLoan') {
    bookingType = 'fixedRateLoan';
  } else if (subProductType === 'OTCVanillaOption') {
    bookingType = 'option';
  } else if (subProductType === 'OTCFloatingRateLoan') {
    bookingType = 'floatingRateLoan';
  }
  state.form = getInitialFormState(bookingType);
  state.form.bookingTypeField = state.form.bookingTypeField.updateValue(bookingType);

  // Resolve template/symbol from reference data — do not use updateValueFromID (depends on
  // availableItems and clears value when the ID is not yet in that list).
  handleTemplateSymbolChange(state, deal.TemplateSymbol, true);
  handleSymbolChange(state, deal.Symbol, true);
  state.form.templateSymbolField = state.form.templateSymbolField.setDisabled(true);
  state.form.symbolField = state.form.symbolField.setDisabled(true);

  state.form.marketField = state.form.marketField.updateValue(deal.MarketAccount);
  state.form.marketAccountField = state.form.marketAccountField.updateValue(deal.MarketAccount);
  state.form.counterpartyField = state.form.counterpartyField.updateValue(deal.Counterparty);

  // Handle SubAccount Allocation setup
  if (deal.Allocation) {
    const allocations: Allocation[] = deal.Allocation.Allocations.map(alloc => ({
      subAccount: alloc.SubAccount,
      value: alloc.Value || '1',
    }));
    state.form.allocationValueTypeField = state.form.allocationValueTypeField.updateValue(deal.Allocation.ValueType);
    state.form.allocations = mapAllocationsToField(allocations, deal.Allocation.ValueType);
  } else if (deal.SubAccount) {
    const allocations: Allocation[] = [{ subAccount: deal.SubAccount, value: '1' }];
    state.form.allocationValueTypeField = state.form.allocationValueTypeField.updateValue(
      AllocationValueTypeEnum.Percentage
    );
    state.form.allocations = mapAllocationsToField(allocations, AllocationValueTypeEnum.Percentage);
  }

  // Initialize deal-type specific fields
  if (isForwardDeal(deal)) {
    initializeForwardDealFields(state, deal);
  } else if (isFixedRateLoanDeal(deal)) {
    initializeFixedRateLoanDealFields(state, deal, productTemplate);
  } else if (isFloatingRateLoanDeal(deal)) {
    initializeFloatingRateLoanDealFields(state, deal, productTemplate);
  } else if (isOptionDeal(deal) && isManualDealOptionSymbol(productTemplate, false)) {
    initializeOptionDealFields(state, deal, productTemplate);
  }

  // Set commission fields (only exist in Forward and Option forms)
  // Use isSystemOverride=true since API returns decimal value, not user-input BPS
  if (isForwardDealFormDraft(state.form) || isOptionDealFormDraft(state.form)) {
    state.form.commissionBpsField = state.form.commissionBpsField.updateValue(deal.DealInfo.CommissionBps, true);
    if (deal.DealInfo?.CommissionCurrency) {
      state.form.commissionCurrencyField = state.form.commissionCurrencyField.updateValue(
        deal.DealInfo.CommissionCurrency
      );
    }
  }

  // Set all fields as touched for validation
  setTouchedValueForAllDealFields(state, true);
  // Validate allocations - all other validations are done by the yup process
  validateAllocations(state);
}

/**
 * Initialize forward deal specific fields for modifying a deal
 */
function initializeForwardDealFields(state: WritableDraft<ManualDealTradeState>, deal: DealForwardData): void {
  if (!isForwardDealFormDraft(state.form) || !isForwardDeal(deal)) {
    return;
  }

  state.form.sideField = state.form.sideField.updateValue(deal.DealSide);

  // Set trade date from deal's TradeTime, preserving original date
  updateModifyDates(deal, state);

  // Set quantity and notional amount based on EnteredQuantityAsset
  const forwardDealInfo = deal.DealInfo;
  let valueForQuantity;
  const enteredQtyAsset = forwardDealInfo.EnteredQuantityAsset;
  if (enteredQtyAsset === undefined || enteredQtyAsset === '') {
    state.form.quantityCurrencyField = state.form.quantityCurrencyField.updateValue('Units');
    valueForQuantity = forwardDealInfo.EnteredQuantity;
  } else {
    state.form.quantityCurrencyField = state.form.quantityCurrencyField.updateValue(forwardDealInfo.NotionalAsset);
    valueForQuantity = forwardDealInfo.NotionalAmount;
  }
  state.form.quantityField = state.form.quantityField.updateValue(
    format(valueForQuantity, {
      truncate: true,
      pretty: false,
      removeTrailingZeros: true,
    })
  );

  state.form.notionalAmountField = state.form.notionalAmountField.updateValue(forwardDealInfo.NotionalAmount);
  state.form.forwardPriceField = state.form.forwardPriceField.updateValue(deal.Price);

  const pricingMode = forwardDealInfo.PricingMode ?? PricingModeEnum.Absolute;
  state.form.pricingModeField = state.form.pricingModeField.updateValue(pricingMode);

  const securityFromRef = deal.Symbol ? state.referenceData.securities?.securitiesBySymbol.get(deal.Symbol) : undefined;
  const forwardSecurity = securityFromRef && isOTCForwardSecurity(securityFromRef) ? securityFromRef : undefined;
  if (!forwardSecurity) {
    logger.warn('initializeForwardDealFields: missing or invalid OTC forward security for deal Symbol', {
      extra: { dealId: deal.ID, dealSymbol: deal.Symbol },
    });
  }

  if (forwardDealInfo.Spread != null && forwardDealInfo.Spread !== '') {
    state.form.spreadField = state.form.spreadField.updateValue(forwardDealInfo.Spread, true);
  }

  if (pricingMode === PricingModeEnum.SpreadToFixing && forwardSecurity) {
    const indexValue = forwardSecurity.ProductDetails.Index;
    if (indexValue) {
      state.form.indexField = state.form.indexField.updateValue(indexValue);
    }
  }

  if (forwardDealInfo.CommissionCalculationType) {
    state.form.commissionCalculationTypeField = state.form.commissionCalculationTypeField.updateValue(
      forwardDealInfo.CommissionCalculationType
    );
  }

  if (forwardDealInfo.InitialMarginPct != null && forwardDealInfo.InitialMarginPct !== '') {
    state.form.initialMarginPercentField = state.form.initialMarginPercentField.updateValue(
      forwardDealInfo.InitialMarginPct,
      true
    );
  }
  if (forwardDealInfo.MaintenanceMarginPct != null && forwardDealInfo.MaintenanceMarginPct !== '') {
    state.form.maintenanceMarginPercentField = state.form.maintenanceMarginPercentField.updateValue(
      forwardDealInfo.MaintenanceMarginPct,
      true
    );
  }
  if (forwardDealInfo.LiquidationMarginPct != null && forwardDealInfo.LiquidationMarginPct !== '') {
    state.form.liquidationMarginPercentField = state.form.liquidationMarginPercentField.updateValue(
      forwardDealInfo.LiquidationMarginPct,
      true
    );
  }
  if (forwardDealInfo.MarginCallPct != null && forwardDealInfo.MarginCallPct !== '') {
    state.form.marginCallPercentField = state.form.marginCallPercentField.updateValue(
      forwardDealInfo.MarginCallPct,
      true
    );
  }
  if (forwardDealInfo.MarginReleasePct != null && forwardDealInfo.MarginReleasePct !== '') {
    state.form.releaseMarginPercentField = state.form.releaseMarginPercentField.updateValue(
      forwardDealInfo.MarginReleasePct,
      true
    );
  }
  if (forwardDealInfo.InitialMarginAsset) {
    state.form.initialMarginCurrencyField = state.form.initialMarginCurrencyField.updateValue(
      forwardDealInfo.InitialMarginAsset
    );
  }

  if (forwardSecurity) {
    const unitNotionalAmount = forwardSecurity.ProductDetails.UnitNotionalAmount;
    if (unitNotionalAmount != null && unitNotionalAmount !== '') {
      state.form.unitNotionalField = state.form.unitNotionalField.updateValue(unitNotionalAmount);
    }
  }
}

/**
 * Initialize fixed rate loan deal specific fields for modifying a deal
 */
function initializeFixedRateLoanDealFields(
  state: WritableDraft<ManualDealTradeState>,
  deal: DealFixedRateLoanData,
  productTemplate: OTCProductTemplateSecurity
): void {
  if (!isFixedRateLoanDealFormDraft(state.form)) {
    return;
  }

  const info = deal.DealInfo;

  state.form.loanSideField = state.form.loanSideField.updateValue(info.Side);

  updateModifyDates(deal, state);

  const enteredQtyAsset = info.EnteredQuantityAsset;
  const useUnits = !enteredQtyAsset || enteredQtyAsset === '' || enteredQtyAsset === 'Units';
  let valueForQuantity: string;
  if (useUnits) {
    state.form.quantityCurrencyField = state.form.quantityCurrencyField.updateValue('Units');
    valueForQuantity = info.EnteredQuantity;
  } else {
    state.form.quantityCurrencyField = state.form.quantityCurrencyField.updateValue(enteredQtyAsset);
    valueForQuantity = info.LoanAmount ?? info.EnteredQuantity;
  }
  state.form.quantityField = state.form.quantityField.updateValue(
    format(valueForQuantity, {
      truncate: true,
      pretty: false,
      removeTrailingZeros: true,
    })
  );

  if (isManualDealFixedRateLoanSymbol(productTemplate, false)) {
    const pd = productTemplate.ProductDetails;
    if (pd.UnitLoanAmount) {
      state.form.unitLoanAmountField = state.form.unitLoanAmountField.updateValue(pd.UnitLoanAmount);
    }
    if (pd.PaymentPeriod) {
      state.form.paymentPeriodField = state.form.paymentPeriodField.updateValue(pd.PaymentPeriod);
    }
  }

  const pricingMode = info.PricingMode ?? PricingModeEnum.Absolute;
  state.form.pricingModeField = state.form.pricingModeField.updateValue(pricingMode);

  if (pricingMode === PricingModeEnum.SpreadToFixing) {
    if (info.Spread != null && info.Spread !== '') {
      state.form.spreadField = state.form.spreadField.updateValue(info.Spread, true);
    }
    if (info.RateSource) {
      state.form.rateSourceField = state.form.rateSourceField.updateValue(info.RateSource);
    }
    state.form.rateField = state.form.rateField.updateValue('');
    if (info.Rate != null && info.Rate !== '') {
      state.form.calculatedRateField = state.form.calculatedRateField.updateValue(info.Rate, true);
    }
  } else {
    state.form.rateField = state.form.rateField.updateValue(info.Rate, true);
    state.form.calculatedRateField = state.form.calculatedRateField.updateValue('');
  }

  if (info.LoanOriginationFeeSpread != null && info.LoanOriginationFeeSpread !== '') {
    state.form.loanOriginationFeeSpreadField = state.form.loanOriginationFeeSpreadField.updateValue(
      info.LoanOriginationFeeSpread,
      true
    );
  }
  if (info.LoanOriginationFee != null && info.LoanOriginationFee !== '') {
    state.form.loanOriginationFeeField = state.form.loanOriginationFeeField.updateValue(info.LoanOriginationFee);
  }

  // LTV / collateral: treat `''` as omitted so we do not overwrite hydrated defaults when DealInfo sends empty placeholders.
  if (info.MinimumLTV != null && info.MinimumLTV !== '') {
    state.form.minimumLTVField = state.form.minimumLTVField.updateValue(info.MinimumLTV, true);
  }
  if (info.MarginCallLTV != null && info.MarginCallLTV !== '') {
    state.form.marginCallLTVField = state.form.marginCallLTVField.updateValue(info.MarginCallLTV, true);
  }
  if (info.LiquidationLTV != null && info.LiquidationLTV !== '') {
    state.form.liquidationLTVField = state.form.liquidationLTVField.updateValue(info.LiquidationLTV, true);
  }
  if (info.CollateralReleaseLTV != null && info.CollateralReleaseLTV !== '') {
    state.form.collateralReleaseLTVField = state.form.collateralReleaseLTVField.updateValue(
      info.CollateralReleaseLTV,
      true
    );
  }
  if (info.InitialCollateralPct != null && info.InitialCollateralPct !== '') {
    state.form.initialCollateralPercentField = state.form.initialCollateralPercentField.updateValue(
      info.InitialCollateralPct,
      true
    );
  }
  if (info.InitialCollateralAmount != null && info.InitialCollateralAmount !== '') {
    state.form.initialCollateralAmountField = state.form.initialCollateralAmountField.updateValue(
      info.InitialCollateralAmount
    );
  }
}

/**
 * Initialize floating rate loan deal specific fields for modifying a deal
 */
function initializeFloatingRateLoanDealFields(
  state: WritableDraft<ManualDealTradeState>,
  deal: DealFloatingRateLoanData,
  productTemplate: OTCProductTemplateSecurity
): void {
  if (!isFloatingRateLoanDealFormDraft(state.form)) {
    return;
  }

  const info = deal.DealInfo;

  state.form.loanSideField = state.form.loanSideField.updateValue(info.Side);

  updateModifyDates(deal, state);

  const enteredQtyAsset = info.EnteredQuantityAsset;
  const useUnits = !enteredQtyAsset || enteredQtyAsset === '' || enteredQtyAsset === 'Units';
  let valueForQuantity: string;
  if (useUnits) {
    state.form.quantityCurrencyField = state.form.quantityCurrencyField.updateValue('Units');
    valueForQuantity = info.EnteredQuantity;
  } else {
    state.form.quantityCurrencyField = state.form.quantityCurrencyField.updateValue(enteredQtyAsset);
    valueForQuantity = info.LoanAmount ?? info.EnteredQuantity;
  }
  state.form.quantityField = state.form.quantityField.updateValue(
    format(valueForQuantity, {
      truncate: true,
      pretty: false,
      removeTrailingZeros: true,
    })
  );

  if (info.LoanAmount != null && info.LoanAmount !== '') {
    state.form.loanAmountField = state.form.loanAmountField.updateValue(info.LoanAmount);
  }

  if (isManualDealFloatingRateLoanSymbol(productTemplate, false)) {
    const pd = productTemplate.ProductDetails;
    if (pd.UnitLoanAmount) {
      state.form.unitLoanAmountField = state.form.unitLoanAmountField.updateValue(pd.UnitLoanAmount);
    }
    if (pd.PaymentPeriod) {
      state.form.paymentPeriodField = state.form.paymentPeriodField.updateValue(pd.PaymentPeriod);
    }
  }

  if (info.Spread != null && info.Spread !== '') {
    state.form.spreadField = state.form.spreadField.updateValue(info.Spread, true);
  }
  if (info.RateSource) {
    state.form.rateSourceField = state.form.rateSourceField.updateValue(info.RateSource);
  }

  if (info.LoanOriginationFeeSpread != null && info.LoanOriginationFeeSpread !== '') {
    state.form.loanOriginationFeeSpreadField = state.form.loanOriginationFeeSpreadField.updateValue(
      info.LoanOriginationFeeSpread,
      true
    );
  }
  if (info.LoanOriginationFee != null && info.LoanOriginationFee !== '') {
    state.form.loanOriginationFeeField = state.form.loanOriginationFeeField.updateValue(info.LoanOriginationFee);
  }

  // LTV / collateral: treat `''` as omitted so we do not overwrite hydrated defaults when DealInfo sends empty placeholders.
  if (info.MinimumLTV != null && info.MinimumLTV !== '') {
    state.form.minimumLTVField = state.form.minimumLTVField.updateValue(info.MinimumLTV, true);
  }
  if (info.MarginCallLTV != null && info.MarginCallLTV !== '') {
    state.form.marginCallLTVField = state.form.marginCallLTVField.updateValue(info.MarginCallLTV, true);
  }
  if (info.LiquidationLTV != null && info.LiquidationLTV !== '') {
    state.form.liquidationLTVField = state.form.liquidationLTVField.updateValue(info.LiquidationLTV, true);
  }
  if (info.CollateralReleaseLTV != null && info.CollateralReleaseLTV !== '') {
    state.form.collateralReleaseLTVField = state.form.collateralReleaseLTVField.updateValue(
      info.CollateralReleaseLTV,
      true
    );
  }
  if (info.InitialCollateralPct != null && info.InitialCollateralPct !== '') {
    state.form.initialCollateralPercentField = state.form.initialCollateralPercentField.updateValue(
      info.InitialCollateralPct,
      true
    );
  }
  if (info.InitialCollateralAmount != null && info.InitialCollateralAmount !== '') {
    state.form.initialCollateralAmountField = state.form.initialCollateralAmountField.updateValue(
      info.InitialCollateralAmount
    );
  }
}

/**
 * Initialize option deal specific fields for modifying a deal
 */
function initializeOptionDealFields(
  state: WritableDraft<ManualDealTradeState>,
  deal: DealOptionData,
  productTemplate: OTCOptionProductTemplate
): void {
  if (!isOptionDealFormDraft(state.form)) {
    return;
  }

  const sideValue = deal.DealInfo.Side || deal.DealSide;
  state.form.sideField = state.form.sideField.updateValue(sideValue);

  // Set trade date from deal's TradeTime, preserving original date
  updateModifyDates(deal, state);

  // Set quantity and notional amount based on EnteredQuantityAsset (aligned with forward modify)
  const optionDealInfo = deal.DealInfo;
  const enteredQtyAsset = optionDealInfo.EnteredQuantityAsset;
  const useUnits = enteredQtyAsset === undefined || enteredQtyAsset === '' || enteredQtyAsset === 'Units';
  let valueForQuantity: string;
  if (useUnits) {
    state.form.quantityCurrencyField = state.form.quantityCurrencyField.updateValue('Units');
    valueForQuantity = optionDealInfo.EnteredQuantity;
  } else {
    state.form.quantityCurrencyField = state.form.quantityCurrencyField.updateValue(optionDealInfo.NotionalAsset);
    valueForQuantity = optionDealInfo.NotionalAmount;
  }
  state.form.quantityField = state.form.quantityField.updateValue(
    format(valueForQuantity, {
      truncate: true,
      pretty: false,
      removeTrailingZeros: true,
    })
  );

  state.form.notionalAmountField = state.form.notionalAmountField.updateValue(optionDealInfo.NotionalAmount);
  state.form.notionalWithCurrencyField.value = state.form.notionalWithCurrencyField.value.updateValue(
    optionDealInfo.NotionalAmount
  );
  state.form.notionalWithCurrencyField.currency = state.form.notionalWithCurrencyField.currency.updateValue(
    optionDealInfo.NotionalAsset
  );

  if (optionDealInfo.Premium != null && optionDealInfo.Premium !== '') {
    state.form.premiumField = state.form.premiumField.updateValue(optionDealInfo.Premium, true);
  }

  const productDetails = productTemplate.ProductDetails;
  if (productDetails.StrikeType === StrikeTypeEnum.Delta) {
    const deltaFromDeal = optionDealInfo.StrikeDelta;
    const deltaFromTemplate = productDetails.StrikeDelta != null ? String(productDetails.StrikeDelta).trim() : '';
    const strikeForForm =
      (deltaFromDeal != null && String(deltaFromDeal).trim() !== '' ? String(deltaFromDeal).trim() : undefined) ??
      (deltaFromTemplate !== '' ? deltaFromTemplate : undefined) ??
      optionDealInfo.StrikePrice;
    if (strikeForForm != null && strikeForForm !== '') {
      state.form.strikePriceField = state.form.strikePriceField.updateValue(strikeForForm, true);
    }
  } else if (optionDealInfo.StrikePrice != null && optionDealInfo.StrikePrice !== '') {
    state.form.strikePriceField = state.form.strikePriceField.updateValue(optionDealInfo.StrikePrice, true);
  }

  if (optionDealInfo.InitialMarginPct != null && optionDealInfo.InitialMarginPct !== '') {
    state.form.initialMarginPercentField = state.form.initialMarginPercentField.updateValue(
      optionDealInfo.InitialMarginPct,
      true
    );
  }
  if (optionDealInfo.MaintenanceMarginPct != null && optionDealInfo.MaintenanceMarginPct !== '') {
    state.form.maintenanceMarginPercentField = state.form.maintenanceMarginPercentField.updateValue(
      optionDealInfo.MaintenanceMarginPct,
      true
    );
  }
  if (optionDealInfo.LiquidationMarginPct != null && optionDealInfo.LiquidationMarginPct !== '') {
    state.form.liquidationMarginPercentField = state.form.liquidationMarginPercentField.updateValue(
      optionDealInfo.LiquidationMarginPct,
      true
    );
  }
  if (optionDealInfo.MarginCallPct != null && optionDealInfo.MarginCallPct !== '') {
    state.form.marginCallPercentField = state.form.marginCallPercentField.updateValue(
      optionDealInfo.MarginCallPct,
      true
    );
  }
  if (optionDealInfo.MarginReleasePct != null && optionDealInfo.MarginReleasePct !== '') {
    state.form.releaseMarginPercentField = state.form.releaseMarginPercentField.updateValue(
      optionDealInfo.MarginReleasePct,
      true
    );
  }
  if (optionDealInfo.InitialMarginAsset) {
    state.form.initialMarginCurrencyField = state.form.initialMarginCurrencyField.updateValue(
      optionDealInfo.InitialMarginAsset
    );
  }

  // Map OptionType string to OptionTypeEnum
  if (productDetails.OptionType === 'Call') {
    state.form.optionTypeField = state.form.optionTypeField.updateValue(OptionTypeEnum.Call);
  } else if (productDetails.OptionType === 'Put') {
    state.form.optionTypeField = state.form.optionTypeField.updateValue(OptionTypeEnum.Put);
  }
}

function updateModifyDates(deal: OTCDealData, state: WritableDraft<ManualDealTradeState>) {
  // Set trade date from deal's TradeTime, fallback to today if missing
  if (deal.DealInfo.TradeTime) {
    const tradeDate = createDateOnly(deal.DealInfo.TradeTime);
    if (tradeDate) {
      state.form.tradeDateField = state.form.tradeDateField.updateValue(tradeDate);
    } else {
      logger.warn('Failed to parse TradeTime to DateOnly:', {
        original: deal.DealInfo.TradeTime,
      });
      // Fallback to today's date since field is non-null
      state.form.tradeDateField = state.form.tradeDateField.updateValue(createDateOnlyToday());
    }
  } else {
    // Fallback to today's date since field is non-null
    state.form.tradeDateField = state.form.tradeDateField.updateValue(createDateOnlyToday());
  }

  // Set effective date from deal's EffectiveTime, fallback to tradeDate if missing
  if (deal.DealInfo.EffectiveTime) {
    const effectiveDate = createDateOnly(deal.DealInfo.EffectiveTime);
    if (effectiveDate) {
      state.form.effectiveDateField = state.form.effectiveDateField.updateValue(effectiveDate);
    } else {
      logger.warn('Failed to parse EffectiveTime to DateOnly:', {
        original: deal.DealInfo.EffectiveTime,
      });
      // Fallback to tradeDate value since field is non-null
      state.form.effectiveDateField = state.form.effectiveDateField.updateValue(state.form.tradeDateField.value);
    }
  } else {
    // Fallback to tradeDate value since field is non-null
    state.form.effectiveDateField = state.form.effectiveDateField.updateValue(state.form.tradeDateField.value);
  }
  state.form.settlementDateField = state.form.settlementDateField.updateValue(deal.DealInfo.SettleTime);

  if (deal.DealInfo.ExpiryTime) {
    state.form.expiryDateField = state.form.expiryDateField.updateValue(deal.DealInfo.ExpiryTime);
  }
}
